import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, HostListener, Input } from '@angular/core';
import { ProjectDurationService } from '../../services/project-duration.service';
import { ChartDataPoint } from '../../models/project-duration.models';
import { ColorHelper } from '@swimlane/ngx-charts';

@Component({
    selector: 'app-project-duration',
    templateUrl: './project-duration.component.html',
    styleUrls: ['./project-duration.component.scss'],
    standalone: false
})
export class ProjectDurationComponent implements OnInit, AfterViewInit {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;

  // Input property to control filter visibility from parent component
  @Input() isFilterVisible: boolean = false;

  // Chart options
  view: any[] = [700, 400];
  showXAxis = true;
  showYAxis = true;
  gradient = false; // No gradient fill
  showLegend = true;
  showXAxisLabel = true;
  xAxisLabel = 'Project Status';
  showYAxisLabel = true;
  yAxisLabel = 'Hours';
  legendTitle = 'Legend';
  legendPosition = 'bottom';
  colorScheme = {
    domain: ['#5AA454', '#E44D25', '#CFC0BB', '#7aa3e5', '#a8385d', '#aae3f5']
  };

  // Data
  data: ChartDataPoint[] = [];
  isLoading = false;
  error: string | null = null;

  // Filter options
  organizationOptions: any[] = [];
  matterOptions: any[] = [];
  projectOptions: any[] = [];
  lpcOptions: any[] = [];
  projectTypeOptions: any[] = [];

  // Selected filter values
  selectedOrganization: any;
  selectedMatter: any;
  selectedProject: any;
  selectedLPC: any;
  selectedProjectType: any;
  
  // Filter visibility - always true as it's controlled by parent through isFilterVisible
  showFilters: boolean = true;

  constructor(private projectDurationService: ProjectDurationService) {}

  ngOnInit(): void {
    this.fetchFilterOptions();
    this.loadData();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.updateChartSize();
    }, 100);
  }

  fetchFilterOptions(): void {
    this.isLoading = true;
    
    // Fetch organizations
    this.projectDurationService.getOrganizationNames()
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.organizationOptions = response.data;
          }
        },
        error: (error) => {
          console.error('Error fetching organizations:', error);
        }
      });
    
    // Only fetch matters if organization is selected
    if (this.selectedOrganization) {
      this.fetchMattersByOrganization(this.selectedOrganization);
    } else {
      // Clear matters if no organization is selected
      this.matterOptions = [];
      this.selectedMatter = null;
      this.projectOptions = [];
      this.selectedProject = null;
    }
    
    // Only fetch projects if matter is selected
    if (this.selectedMatter) {
      this.fetchProjectsByMatter(this.selectedMatter);
    } else {
      // Clear projects if no matter is selected
      this.projectOptions = [];
      this.selectedProject = null;
    }
    
    // Fetch project types
    this.projectDurationService.getProjectTypes()
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.projectTypeOptions = response.data;
          }
        },
        error: (error) => {
          console.error('Error fetching project types:', error);
        }
      });
    
    // Fetch LPC users
    this.projectDurationService.getLPCUsers({
      "roles": ["LPC"],
      "userTypes": []
    })
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.lpcOptions = response.data;
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error fetching LPC users:', error);
          this.isLoading = false;
        }
      });
  }
  
  /**
   * Fetch matters based on selected organization
   */
  fetchMattersByOrganization(organization: string): void {
    this.isLoading = true;
    this.projectDurationService.getMatterNames(organization)
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.matterOptions = response.data;
            console.log(`Matters loaded for ${organization}:`, this.matterOptions);
          } else {
            this.matterOptions = [];
            console.warn(`No matters found for ${organization}`);
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error(`Error loading matters for ${organization}:`, error);
          this.matterOptions = [];
          this.isLoading = false;
        }
      });
  }
  
  /**
   * Fetch projects based on selected matter
   */
  fetchProjectsByMatter(matter: string): void {
    this.isLoading = true;
    this.projectDurationService.getAllProjects(matter)
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.projectOptions = response.data;
            console.log(`Projects loaded for ${matter}:`, this.projectOptions);
          } else {
            this.projectOptions = [];
            console.warn(`No projects found for ${matter}`);
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error(`Error loading projects for ${matter}:`, error);
          this.projectOptions = [];
          this.isLoading = false;
        }
      });
  }

  @HostListener('window:resize')
  onResize() {
    this.updateChartSize();
  }

  private updateChartSize(): void {
    if (this.chartContainer) {
      const width = this.chartContainer.nativeElement.offsetWidth;
      // Vertical bar charts: fixed height works well for comparison
      const chartHeight = 350; // Compact but readable
      this.view = [width, chartHeight];
    }
  }

  private loadData(): void {
    this.isLoading = true;
    this.error = null;

    const filters = {
      organization: this.selectedOrganization,
      matter: this.selectedMatter,
      project: this.selectedProject,
      lpc: this.selectedLPC,
      projectType: this.selectedProjectType
    };

    this.projectDurationService.getChartData(filters).subscribe({
      next: (data) => {
        this.data = data;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading project duration data:', err);
        this.error = 'Failed to load project duration data';
        this.isLoading = false;
      }
    });
  }

  applyFilters(): void {
    this.loadData();
  }

  clearFilters(): void {
    this.selectedOrganization = null;
    this.selectedMatter = null;
    this.selectedProject = null;
    this.selectedLPC = null;
    this.selectedProjectType = null;
    this.loadData();
  }
  
  /**
   * Toggle filter visibility
   * Note: This method is now controlled by the parent component through isFilterVisible
   */
  toggleFilters(): void {
    // This method is now deprecated as filter visibility is controlled by the parent
    // through the isFilterVisible input property
    this.showFilters = true; // Always true as visibility is controlled by isFilterVisible
  }

  onOrganizationChange(event: any): void {
    console.log('Organization changed:', event);
    
    // Reset matter and project selections when organization changes
    this.selectedMatter = null;
    this.selectedProject = null;
    
    // Clear project options
    this.projectOptions = [];
    
    // Fetch matters for the selected organization
    if (this.selectedOrganization) {
      this.fetchMattersByOrganization(this.selectedOrganization);
    } else {
      // Clear matters if no organization is selected
      this.matterOptions = [];
    }
  }

  onMatterChange(event: any): void {
    console.log('Matter changed:', event);
    
    // Reset project selection when matter changes
    this.selectedProject = null;
    
    // Fetch projects for the selected matter
    if (this.selectedMatter) {
      this.fetchProjectsByMatter(this.selectedMatter);
    } else {
      // Clear projects if no matter is selected
      this.projectOptions = [];
    }
  }

  onProjectChange(event: any): void {
    // Handle project change if needed
  }

  onLPCChange(event: any): void {
    // Handle LPC change if needed
  }

  onProjectTypeChange(event: any): void {
    // Handle project type change if needed
  }

  onSelect(event: any): void {
    // Handle chart selection if needed
    console.log('Item selected', event);
  }
}
